import React, { useMemo, useState, useCallback } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'
import { useNavigate } from 'react-router-dom'

import { YStack, XStack, View, getTokenValue, type Token } from 'tamagui'

import { useInEmbed } from '../Embed'

import { Link } from '../Link'
import { H3, Paragraph } from '../Text'
import { Card } from '../Card'
import { Image } from '../Image'
import { Button } from '../Button/Button'
import { FormattedMarkdown } from '../FormattedMarkdown/FormattedMarkdown'
import { Tooltip } from '../Tooltip'
import { UserPrimaryPhoto } from './UserPrimaryPhoto'
import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'
import { Icon } from '../Icon'
import { UserSocialProfileLinks } from '../SocialProfileLinks/UserSocialProfileLinks'

import type { UserPrimaryPhotoNew_user$key } from './__generated__/UserPrimaryPhotoNew_user.graphql'
import type { UserCardNew_user$key } from './__generated__/UserCardNew_user.graphql'
import type { UserCardNew_GuildMemberships$key } from './__generated__/UserCardNew_GuildMemberships.graphql'
import type { UserCardNew_BadgeLink$key } from './__generated__/UserCardNew_BadgeLink.graphql'

const ALLOWED_ELEMENTS = ['a', 'p']

// CSS gradient fallbacks for users without a background photo
// (same approach as legacy UserBackgroundPhoto)
const DEFAULT_BACKGROUNDS = [
  'radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(65.03% 49.37% at 67.2% -1.1%, rgba(97, 227, 255, 0.7) 0%, rgba(97, 227, 255, 0) 100%), radial-gradient(73.71% 91.32% at 0% 100%, rgba(228, 213, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(62.83% 107.44% at 97.15% 97.09%, rgba(228, 79, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(99.64% 75.06% at 8.54% 18.33%, rgba(85, 51, 235, 0.7) 0%, rgba(85, 51, 235, 0) 100%), #E5ADFF',
  'radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(62.83% 107.44% at 97.15% 97.09%, rgba(228, 79, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%), radial-gradient(99.64% 75.06% at 8.54% 18.33%, rgba(85, 51, 235, 0.7) 0%, rgba(85, 51, 235, 0) 100%), linear-gradient(0deg, #E5ADFF, #E5ADFF), radial-gradient(114% 86.44% at 93.08% -9.01%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(65.03% 49.37% at 67.2% -1.1%, rgba(97, 227, 255, 0.7) 0%, rgba(97, 227, 255, 0) 100%), radial-gradient(73.71% 91.32% at 0% 100%, rgba(228, 213, 79, 0.7) 0%, rgba(228, 79, 79, 0) 100%)',
]

function pickFromDefaults(rowId: string | undefined, defaults: string[]) {
  const id = rowId?.substring(0, 8) || '0'
  return defaults[parseInt(id, 16) % defaults.length]
}

const getNumericTokenValue = (
  token: Token,
  group: 'size' | 'space'
): number => {
  const value: unknown = getTokenValue(token, group)
  return typeof value === 'number' ? value : Number(value)
}

export function UserCard({
  user,
  limitDescriptionLines = 4,
  showBadges = true,
  expand = false,
  children,
}: {
  user: UserCardNew_user$key
  limitDescriptionLines?: number
  showBadges?: boolean
  expand?: boolean
  children?: React.ReactNode
}) {
  const data = useFragment(
    graphql`
      fragment UserCardNew_user on User {
        rowId
        slugId
        firstName
        lastName
        bio

        isMe

        ...UserSocialProfileLinks_user
        ...UserPrimaryPhotoNew_user

        backgroundPhoto {
          transformUrl(transform: { w: 500, h: 167, dpr: 3, fit: "cover" })
        }

        organizerGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: ORGANIZER }
        ) {
          ...UserCardNew_GuildMemberships
        }
        editorGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: EDITOR }
        ) {
          ...UserCardNew_GuildMemberships
        }
      }
    `,
    user
  )

  const inEmbed = useInEmbed()

  const content = (
    <Card glass padding={0} flex={expand ? 1 : undefined}>
      <UserCardPhotos
        rowId={data.rowId}
        firstName={data.firstName}
        lastName={data.lastName}
        backgroundPhotoUrl={data.backgroundPhoto?.transformUrl ?? null}
        userFragmentRef={data}
        badges={
          showBadges ? (
            <BadgesSection
              organizerGuildMemberships={data.organizerGuildMemberships}
              editorGuildMemberships={data.editorGuildMemberships}
              small
            />
          ) : null
        }
      />

      <YStack
        gap='$4'
        paddingVertical='$4'
        paddingHorizontal='$5'
        flex={expand ? 1 : undefined}
      >
        <H3 size='$6'>
          {data.firstName} {data.lastName}
        </H3>

        {!!data.bio && (
          <YStack flex={expand ? 1 : undefined}>
            <FormattedMarkdown
              allowedElements={ALLOWED_ELEMENTS}
              formattedVariant='PLAIN_TEXT'
              numberOfLines={limitDescriptionLines}
              textSize='$4'
            >
              {data.bio}
            </FormattedMarkdown>
          </YStack>
        )}

        <UserSocialProfileLinks user={data} />

        {children}

        {inEmbed && !data.isMe && (
          <Button
            tag='a'
            href={`/${data.slugId}`}
            secondary
            height={34}
            borderRadius={17}
          >
            <Button.Text size='$4' secondary>
              View {data.firstName}&apos;s Profile
            </Button.Text>
          </Button>
        )}

        {!inEmbed && data.isMe && (
          <XStack
            justifyContent='flex-end'
            alignItems='center'
            marginTop='$1'
            height={16}
          >
            <Link
              href={`/${data.slugId}/settings`}
              hoverStyle={{ textDecorationLine: 'none' }}
              // The card navigates to the profile on press; without this the
              // press bubbles up and sends the user straight back there.
              onPress={(event) => event.stopPropagation()}
            >
              <Button
                borderRadius={15}
                marginRight={-11}
                height={30}
                minHeight={30}
                paddingRight='$3'
                paddingLeft='$2'
                secondary
              >
                <XStack gap='$1' alignItems='center'>
                  <Icon size='$4' name='settings-filled' />

                  <Button.Text size='$4' secondary>
                    <Trans>Settings</Trans>
                  </Button.Text>
                </XStack>
              </Button>
            </Link>
          </XStack>
        )}
      </YStack>
    </Card>
  )

  const { t } = useLingui()
  const navigate = useNavigate()

  return (
    <View
      display='flex'
      flex={expand ? 1 : undefined}
      flexDirection='column'
      cursor='pointer'
      role='link'
      tabIndex={0}
      aria-label={t`${data.firstName} ${data.lastName}`}
      onPress={() => navigate(`/${data.slugId}`)}
      // @ts-expect-error color is needed for text inheritance on web
      color='$colorStrong'
    >
      {content}
    </View>
  )
}

function UserCardPhotos({
  rowId,
  firstName,
  lastName,
  backgroundPhotoUrl,
  userFragmentRef,
  badges,
}: {
  rowId: string
  firstName: string | null
  lastName: string | null
  backgroundPhotoUrl: string | null
  userFragmentRef: UserPrimaryPhotoNew_user$key
  badges?: React.ReactNode
}) {
  const backgroundGradient = useMemo(() => {
    if (backgroundPhotoUrl) return null
    return pickFromDefaults(rowId, DEFAULT_BACKGROUNDS)
  }, [backgroundPhotoUrl, rowId])
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || 'User'

  return (
    <YStack maxWidth='100%'>
      {backgroundPhotoUrl ? (
        <Image
          alt={`Background Photo for ${displayName}`}
          src={backgroundPhotoUrl}
          aspectRatio={500 / 167}
          borderTopLeftRadius='$6'
          borderTopRightRadius='$6'
          maxHeight={30 * 4}
          maxWidth='100%'
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
      ) : (
        <View
          borderTopLeftRadius='$6'
          borderTopRightRadius='$6'
          height={30 * 4}
          maxWidth='100%'
          style={{ background: backgroundGradient ?? undefined }}
        />
      )}

      <XStack marginTop={-40} paddingHorizontal='$5' alignItems='flex-end'>
        <UserPrimaryPhoto
          user={userFragmentRef}
          size='$20'
          // @ts-expect-error boxShadow is web-only
          boxShadow='0 0 0 3px var(--background)'
        />
        {badges}
      </XStack>
    </YStack>
  )
}

const MEMBERSHIPS_FRAGMENT = graphql`
  fragment UserCardNew_GuildMemberships on GuildMembershipsConnection {
    edges {
      node {
        id
        ...UserCardNew_BadgeLink
      }
    }
  }
`

function BadgesSection({
  organizerGuildMemberships,
  editorGuildMemberships,
  small = false,
}: {
  organizerGuildMemberships: UserCardNew_GuildMemberships$key
  editorGuildMemberships: UserCardNew_GuildMemberships$key
  small?: boolean
}) {
  const organizerData = useFragment(
    MEMBERSHIPS_FRAGMENT,
    organizerGuildMemberships
  )
  const editorData = useFragment(MEMBERSHIPS_FRAGMENT, editorGuildMemberships)

  const allEdges = useMemo(() => {
    return [...organizerData.edges, ...editorData.edges]
      .filter((e) => e.node)
      .slice(0, 6)
  }, [organizerData, editorData])

  const [maxVisible, setMaxVisible] = useState(allEdges.length)

  const badgeSize = getNumericTokenValue(small ? '$7' : '$10', 'size')
  const gapSize = getNumericTokenValue(small ? '$2' : '$3', 'space')

  const onLayout = useCallback(
    (e: { nativeEvent: { layout: { width: number } } }) => {
      const width = e.nativeEvent.layout.width
      const count = Math.max(
        1,
        Math.floor((width + gapSize) / (badgeSize + gapSize))
      )
      setMaxVisible((previousCount) =>
        previousCount === count ? previousCount : count
      )
    },
    [badgeSize, gapSize]
  )

  if (!allEdges.length) return null

  const visibleEdges = allEdges.slice(0, maxVisible)

  return (
    <XStack
      flex={1}
      minWidth={0}
      onLayout={onLayout}
      justifyContent='flex-end'
      paddingBottom='$2'
    >
      <XStack
        gap={small ? '$2' : '$3'}
        alignItems='flex-start'
        flexWrap='nowrap'
      >
        {visibleEdges.map((edge) => {
          if (!edge.node) return null
          return (
            <BadgeLink
              key={edge.node.id}
              membership={edge.node}
              small={small}
            />
          )
        })}
      </XStack>
    </XStack>
  )
}

function BadgeLink({
  membership,
  small = false,
}: {
  membership: UserCardNew_BadgeLink$key
  small?: boolean
}) {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment UserCardNew_BadgeLink on GuildMembership {
        membershipLevel
        guild {
          slugId
          name
          ...GuildPrimaryPhotoNew_guild
        }
      }
    `,
    membership
  )

  if (!data.guild) return null

  const photoSize = small ? '$7' : '$10'
  const badgeSize = getNumericTokenValue(photoSize, 'size')
  const iconContainerSize = getNumericTokenValue(small ? '$4' : '$5', 'size')
  const { membershipLevel } = data

  const roleLabel =
    membershipLevel === 'ORGANIZER'
      ? t`Organizer`
      : membershipLevel === 'EDITOR'
        ? t`Editor`
        : t`Member`
  const tooltipText = `${roleLabel} of ${data.guild.name}`

  return (
    <Tooltip placement='top' delay={200}>
      <Tooltip.Trigger>
        <Link
          href={`/${data.guild.slugId}`}
          // Keep the card's own press from replacing this navigation
          onPress={(event) => event.stopPropagation()}
        >
          <View height={badgeSize} opacity={0.7} hoverStyle={{ opacity: 1 }}>
            <GuildPrimaryPhoto guild={data.guild} size={photoSize} />

            {(membershipLevel === 'ORGANIZER' ||
              membershipLevel === 'EDITOR') && (
              <View
                position='absolute'
                bottom={-iconContainerSize * 0.25}
                left={badgeSize - iconContainerSize * 0.75}
                width={iconContainerSize}
                height={iconContainerSize}
                alignItems='center'
                justifyContent='center'
                borderRadius={iconContainerSize / 2}
                // @ts-expect-error web-only styles
                boxShadow='inset 0px 0px 0px 3px rgba(27, 19, 64, 0.2)'
                style={{
                  background:
                    'linear-gradient(0deg, #5533EB 28.13%, rgba(85, 51, 235, 0) 159.37%), #FFFFFF',
                }}
              >
                <Icon
                  name={
                    membershipLevel === 'ORGANIZER'
                      ? 'star-filled'
                      : 'ribbon-filled'
                  }
                  size={small ? '$2' : '$3'}
                  color='$colorOnPrimary'
                />
              </View>
            )}
          </View>
        </Link>
      </Tooltip.Trigger>

      <Tooltip.Content>
        <Tooltip.Arrow />
        <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
          {tooltipText}
        </Paragraph>
      </Tooltip.Content>
    </Tooltip>
  )
}
